import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import QuestionIcon from "@redotech/redo-web/icon-old/question.svg";
import { produce } from "immer";
import * as React from "react";
import { StepId, StepType, StepTypeDetailsProps } from "../step";

export interface ChoiceState {
  next: StepId | undefined;
  name: string;
  code?: string;
  additionalAiInfo?: string;
}

export interface MultipleChoiceState {
  choices: ChoiceState[];
  prompt: string;
  description: string;
  reason: boolean;
  shuffle?: boolean;
  attempted?: boolean;
  customTitle?: string;
  additionalAiInfo?: string;
}

export const MULTIPLE_CHOICE = (
  Details: React.ComponentType<StepTypeDetailsProps<MultipleChoiceState>>,
): StepType<MultipleChoiceState, ModelStep.MultipleChoice> => ({
  Details: (props: StepTypeDetailsProps<MultipleChoiceState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Multiple choice",
  valid(state) {
    return (
      !!state.prompt &&
      !!state.choices.length &&
      state.choices.every((choice) => choice.name && choice.next)
    );
  },
  Icon: QuestionIcon,
  description(state) {
    return state.prompt;
  },
  downstream(state) {
    // Map all choices to preserve semantic indices (don't filter)
    // Each choice's position in the array corresponds to its handle index
    return state.choices.map((choice) => ({
      id: choice.next,
      label: choice.name,
    }));
  },
  maxDownstream(state) {
    return state.choices.length;
  },
  empty: {
    choices: [],
    prompt: "",
    description: "",
    reason: false,
    shuffle: true,
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      for (const choice of state.choices) {
        if (choice.next === stepId) {
          choice.next = undefined;
        }
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    return produce(state, (draft) => {
      if (handleIndex >= 0 && handleIndex < draft.choices.length) {
        const choice = draft.choices[handleIndex];
        if (choice) {
          choice.next = targetStepId;
        }
      } else {
        while (draft.choices.length <= handleIndex) {
          draft.choices.push({
            next: undefined,
            name: `Choice ${draft.choices.length + 1}`,
          });
        }
        const choice = draft.choices[handleIndex];
        if (choice) {
          choice.next = targetStepId;
        }
      }
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      for (const choice of draft.choices) {
        if (choice.next === targetStepId) {
          choice.next = undefined;
        }
      }
    });
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.MULTIPLE_CHOICE,
      message: state.prompt,
      description: state.description,
      options: state.choices.map((choice) => ({
        name: choice.name,
        next: choice.next ? id(choice.next) : undefined,
        code: choice.code,
        additionalAiInfo: choice.additionalAiInfo,
      })),
      reason: state.reason,
      shuffle: state.shuffle,
      additionalAiInfo: state.additionalAiInfo,
    };
  },
  fromModel(model, id) {
    return {
      customTitle: model.customTitle,
      choices: model.options.map((option) => ({
        name: option.name,
        next: option.next ? id(option.next) : undefined,
        code: option.code,
        additionalAiInfo: option.additionalAiInfo,
      })),
      prompt: model.message,
      description: model.description ?? "",
      reason: model.reason,
      shuffle: model.shuffle,
      additionalAiInfo: model.additionalAiInfo,
    };
  },
});

export function getReturnReasonStep(
  returnReasonTitle: string,
  multipleChoiceDetails: React.ComponentType<
    StepTypeDetailsProps<MultipleChoiceState>
  >,
): StepType<MultipleChoiceState, ModelStep.MultipleChoice> {
  return {
    ...MULTIPLE_CHOICE(multipleChoiceDetails),
    title: returnReasonTitle,
    toModel(state, id) {
      return {
        ...MULTIPLE_CHOICE(multipleChoiceDetails).toModel(state, id),
        reason: true,
      };
    },
  };
}
